import { ReactNode, MouseEvent } from 'react';
import cn from 'classnames';
import ReactTooltip from 'react-tooltip';

import styles from './Button.module.scss';

export interface IButtonProps {
  id?: string;
  text: ReactNode;
  type?: 'submit' | 'reset' | 'button';
  color?: 'default' | 'primary' | 'danger' | 'warning' | 'info' | 'success' | 'load' | 'loadLink';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  name?: string;
  variant?: 'minimal' | 'contained' | 'link' | 'linkButton';
  visuallyDisabled?: boolean;
  disabled?: boolean;
  startIcon?: string;
  endArrow?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  form?: string;
  tooltip?: string;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
}

const Button = ({
  id,
  color = 'default',
  size = 'large',
  className = '',
  variant = 'contained',
  tooltip = '',
  text,
  startIcon,
  endArrow,
  visuallyDisabled,
  disabled,
  ...rest
}: IButtonProps) => {
  const colorClassName = `Button${color.charAt(0).toUpperCase()}${color.slice(1)}`;
  let classNames = cn([
    styles.Button,
    styles[colorClassName],
    className,
    colorClassName,
    {
      [styles.ButtonSmall]: size === 'small',
      [styles.ButtonMedium]: size === 'medium',
      [styles.ButtonLarge]: size === 'large',
    },
    {
      [styles.ButtonMinimal]: variant === 'minimal',
      [styles.ButtonContained]: variant === 'contained',
      [styles.ButtonLink]: variant === 'link',
      [styles.ButtonLinkButton]: variant === 'linkButton',
      [styles.ButtonDisabled]: visuallyDisabled || disabled,
    },
  ]);

  if (endArrow) {
    classNames = cn([classNames, styles.ButtonWithCaret]);
  }

  return (
    <>
      <button
        className={classNames}
        disabled={disabled}
        id={id}
        {...(tooltip ? { 'data-for': `button-tooltip-${id}` } : {})}
        {...(tooltip ? { 'data-tip': 'React-tooltip' } : {})}
        {...rest}
      >
        {startIcon ? <span className={cn([styles.StartIcon, startIcon])} /> : null}
        {text}
        {endArrow && <span className={styles.Caret} />}
      </button>
      {tooltip && (
        <ReactTooltip className={styles.Tooltip} type="dark" effect="solid" id={`button-tooltip-${id}`}>
          <span>{tooltip}</span>
        </ReactTooltip>
      )}
    </>
  );
};

export { Button };
